import { createContext } from 'react';
import type Maybe from 'true-myth/maybe';
import { z } from 'zod';

import type { Clock } from '../clock/clock';
import type { Customization } from '../customization/customization';
import type { SkillDrawerStates } from '../data-contract/skill-drawer';
import type { UserMe } from '../data-contract/user';
import type { UserSettings } from '../data-contract/user-settings-schema';
import type { UserUiSettings } from '../data-contract/user-ui-settings';
import type { AppEnvironment } from '../environment/environment';
import type { FeatureFlagService } from '../feature-flag-service/feature-flag-service';
import type { HttpFetcherWithBaseUrl } from '../http-fetcher';
import type { LanguageCode, LanguageConfig, LanguageConfigMap, LanguageKey } from '../internationalization';
import type { Translate } from '../internationalization/translation';
import type { Logger } from '../logger/logger';
import type { RealtimeEventListenerBuilder } from '../realtime-event-listener/realtime-event-listener';
import type { AppTheme } from '../styles/theme';
import type { ValidatingHttpFetcherWithBaseUrl } from '../validating-http-fetcher';

type ParamKeyValuePair = readonly [string, string];

type URLSearchParamsInit =
    | Record<string, string | readonly string[]>
    | URLSearchParams
    | string
    | readonly ParamKeyValuePair[];

type NavigateOptions = {
    readonly preventScrollReset?: boolean;
    readonly relative?: 'path' | 'route';
    readonly replace?: boolean;
    // biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration
    readonly state?: any;
    readonly unstable_flushSync?: boolean;
    readonly unstable_viewTransition?: boolean;
};

type SetURLSearchParams = (
    nextInit?: URLSearchParamsInit | ((prev: URLSearchParams) => URLSearchParamsInit),
    navigateOpts?: NavigateOptions,
) => void;

type Path = {
    /**
     * A URL pathname, beginning with a /.
     */
    readonly hash: string;
    /**
     * A URL search string, beginning with a ?.
     */
    readonly pathname: string;
    /**
     * A URL fragment identifier, beginning with a #.
     */
    readonly search: string;
};

type To = Partial<Path> | string;

type NavigateFunction = {
    (to: To, options?: NavigateOptions): void;
    (delta: number): void;
};

type ApplicationContextDependencies = {
    readonly cobrainerApiFetcher: HttpFetcherWithBaseUrl;
    readonly fetchOidcConfig?: () => void;
    readonly logger: Logger;
    readonly realtimeApiFetcher: HttpFetcherWithBaseUrl;
    readonly realtimeEventListenerBuilder: RealtimeEventListenerBuilder;
    readonly translate: Translate;
    readonly validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl;
    readonly validatingRealtimeApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

export const onboardingStatusSchema = z
    .union([z.literal('finished'), z.literal('never'), z.literal('pending')])
    .catch('never');
export type OnboardingStatus = z.infer<typeof onboardingStatusSchema>;

export type ApplicationState = {
    readonly availableLanguages: LanguageConfigMap;
    readonly clock: Clock;
    readonly customization: Maybe<Customization>;
    readonly featureFlagService: FeatureFlagService;
    readonly hideLogoutButtons: boolean;
    readonly isAssumedTenant: boolean;
    readonly isDefaultCustomization: boolean;
    readonly isPendingMyUserProfile: boolean;
    readonly isSmallScreen: boolean;
    readonly language: LanguageConfig;
    readonly languageKey: LanguageKey;
    readonly location: Pick<Location, 'hash' | 'pathname' | 'search'>;
    readonly locationSearchParams: URLSearchParams;
    readonly myUserProfile: Maybe<UserMe>;
    readonly onboardingStatus: OnboardingStatus;
    readonly skillDrawerStates: SkillDrawerStates | null;
    readonly uiSettings: Maybe<UserUiSettings>;
    readonly uiTheme: AppTheme;
    readonly userIsAnonymized: boolean;
    readonly userIsBlocked: boolean;
    readonly userIsLoggedIn: boolean;
    readonly userSettings: Maybe<UserSettings>;
};

export type ApplicationActions = {
    readonly deleteLocationSearchParams: (paramsToDelete: readonly string[]) => void;
    readonly login: (providerId: number) => void;
    readonly logout: () => void;
    readonly mailTo: ({
        body,
        email,
        subject,
    }: {
        readonly body: string;
        readonly email: string;
        readonly subject: string;
    }) => void;
    readonly navigateBack: () => void;
    readonly navigateTo: NavigateFunction;
    readonly openSkillDetails: (skillId: string) => void;
    readonly openWindow: (url: string) => void;
    readonly refetchMyUserProfile: () => void;
    readonly resetCustomization: () => void;
    readonly setCurrentLocationToLastBrowserHistoryItem: () => void;
    readonly setLanguage: (languageCode: LanguageCode) => void;
    readonly setLocationSearchParams: SetURLSearchParams;
    readonly setOnboardingStatus: (status: 'finished' | 'pending') => void;
    readonly setSkillDrawerStates: (skillDrawerStates: SkillDrawerStates | null) => void;
    readonly toggleAnonymization: () => void;
};

export type ApplicationContextType = ApplicationContextDependencies & {
    readonly actions: ApplicationActions;
    readonly applicationState: ApplicationState;
    readonly environment: AppEnvironment;
};

export const ApplicationContext = createContext<ApplicationContextType>({} as ApplicationContextType);
